import { useState, useCallback, useRef } from 'react';

interface UseOpenStateOptions {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
}

export function useOpenState({
  open,
  defaultOpen,
  onOpenChange,
  trackAnalytics,
}: UseOpenStateOptions) {
  const initialOpen = open !== undefined ? open : !!defaultOpen;
  const [minimizedState, setMinimizedState] = useState(!initialOpen);
  const minimized = open !== undefined ? !open : minimizedState;
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const updateOpen = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        previousFocusRef.current = document.activeElement as HTMLElement | null;
      }
      onOpenChange?.(newOpen);
      if (open === undefined) setMinimizedState(!newOpen);
      trackAnalytics('chat_toggle', { open: newOpen });
    },
    [onOpenChange, open, trackAnalytics]
  );

  const openChat = useCallback(() => updateOpen(true), [updateOpen]);
  const closeChat = useCallback(() => {
    updateOpen(false);
    // Restore focus when closing
    setTimeout(() => {
      previousFocusRef.current?.focus();
    }, 0);
  }, [updateOpen]);
  const toggleChat = useCallback(() => updateOpen(minimized), [updateOpen, minimized]);

  return { minimized, openChat, closeChat, toggleChat, previousFocusRef };
}
